import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import PropTypes from 'prop-types';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import Container from './Container';
import Text from './Text';
import EmptyButton from './EmptyButton';
import Icon from './Icon';

let timeout;
let touchMoveTimeout;
let prevPageX;
let prevPageY;
let directionX;
let directionY;

const translate = (mode, value) =>
  mode === 'desktop' ? `translateX(-${value})` : `translateY(${value})`;

const slideIn = mode => keyframes`
    from {
        transform: ${translate(mode, '100%')};
    }
    to {
        transform: ${translate(mode, '0')};
    }
`;

const slideOut = mode => keyframes`
    from {
        transform: ${translate(mode, '0')};
    }
    to {
        transform: ${translate(mode, '100%')};
    }
`;

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const fadeOut = keyframes`
    from {
        opacity: 1;
    }
    to {
        opacity: 0;
    }
`;

const slideAnimation = ({ hide, animationDuration, mode }) =>
  css`
    ${hide ? slideOut(mode) : slideIn(mode)} ${animationDuration}ms
  `;

const fadeAnimation = ({ hide, animationDuration }) =>
  css`
    ${hide ? fadeOut : fadeIn} ${animationDuration}ms
  `;

const AnimationContainer = styled(Container)`
  animation: ${slideAnimation};
  transform: ${props =>
    props.hide ? translate(props.mode, '100%') : translate(props.mode, '0')};
`;

const AnimationButton = styled(EmptyButton)`
  animation: ${fadeAnimation};
  opacity: ${props => (props.hide ? '0' : '1')};
`;

const NotificationSlider = ({
  show,
  duration,
  animationDuration,
  children,
  icon,
  heading,
  text,
  onClose,
  mode,
  zIndex,
  ...props
}) => {
  const [inDom, setInDom] = useState(false);

  const close = useCallback(() => {
    const elm = document.querySelector('.NotificationSlider__container');
    enableBodyScroll(elm);
    clearTimeout(timeout);
    onClose();
  }, [onClose]);

  const startTimer = useCallback(() => {
    timeout = setTimeout(close, duration);
  }, [close, duration]);

  useEffect(() => {
    if (show && !inDom) {
      setInDom(true);
      startTimer();
    }
    if (!show && inDom) {
      setTimeout(() => {
        setInDom(false);
      }, animationDuration);
    }
  }, [show, animationDuration, startTimer, inDom, mode]);

  useEffect(() => {
    if (inDom && mode !== 'desktop') {
      const elm = document.querySelector('.NotificationSlider__container');
      disableBodyScroll(elm);
    }
  }, [inDom, mode]);

  const onTouchMove = e => {
    const {
      touches: [touch],
    } = e;

    const { pageX, pageY } = touch;

    if (touchMoveTimeout) {
      return;
    }

    touchMoveTimeout = setTimeout(() => {
      if (prevPageX) {
        const distanceX = pageX - prevPageX;
        const distanceY = pageY - prevPageY;
        directionX = distanceX < 0 ? 'right' : 'left';
        directionY = distanceY < 0 ? 'down' : 'up';
      }

      prevPageX = pageX;
      prevPageY = pageY;
      touchMoveTimeout = null;
    }, 10);
  };

  const onTouchEnd = () => {
    if (mode === 'desktop' && directionX === 'right') {
      close();
    }
    if (mode !== 'desktop' && directionY === 'up') {
      close();
    }
  };

  const defaultRender = () => (
    <Container
      display="flex"
      flexDirection={mode === 'desktop' ? 'row' : 'column'}
      alignItems="center"
    >
      {icon ? (
        <Container maxWidth={mode === 'desktop' ? '130px' : 'unset'}>
          {icon}
        </Container>
      ) : null}
      <Container
        flex="1"
        padding={mode === 'desktop' ? '20px 0 20px 20px' : '20px 0'}
        alignItems="center"
      >
        {heading ? (
          <Text
            display="block"
            fontSize={mode === 'desktop' ? '16px' : '18px'}
            fontWeight="600"
            color="normal"
            marginBottom="12px"
            lineHeight="1.3"
            textAlign={mode === 'desktop' ? 'left' : 'center'}
          >
            {heading}
          </Text>
        ) : null}
        {text ? (
          <Text
            fontSize={mode === 'desktop' ? '14px' : '16px'}
            color="text400"
            lineHeight="1.4"
            display="block"
            textAlign={mode === 'desktop' ? 'left' : 'center'}
          >
            {text}
          </Text>
        ) : null}
      </Container>
      {mode === 'desktop' && (
        <Container marginBottom="auto">
          <EmptyButton padding="5px" onClick={close}>
            <Icon iconSize="medium" className="ls-icon icon-generalxlarge" />
          </EmptyButton>
        </Container>
      )}
    </Container>
  );

  if (!inDom) return null;

  return createPortal(
    <Container
      zIndex={zIndex}
      position="relative"
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {mode !== 'desktop' && (
        <AnimationButton
          onClick={close}
          position="fixed"
          left="0"
          top="0"
          right="0"
          bottom="0"
          backgroundColor="rgba(0,0,0,0.5)"
          width="100%"
          height="100%"
          animationDuration={animationDuration}
          hide={!show}
        />
      )}
      <AnimationContainer
        position="fixed"
        padding={mode === 'desktop' ? '20px' : '0'}
        maxWidth={mode === 'desktop' ? '465px' : 'unset'}
        width="100%"
        left="0"
        bottom="0"
        hide={!show}
        animationDuration={animationDuration}
        mode={mode}
        {...props}
      >
        <Container
          width="100%"
          backgroundColor="white"
          borderRadius={mode === 'desktop' ? '10px' : '27px'}
          borderBottomLeftRadius={mode === 'desktop' ? '10px' : '0'}
          borderBottomRightRadius={mode === 'desktop' ? '10px' : '0'}
          padding={mode === 'desktop' ? '0' : '0 30px 30px 30px'}
          boxShadow="0 0 20px 0 rgba(0, 0, 0, 0.2)"
          className="NotificationSlider__container"
        >
          {children || defaultRender()}
        </Container>
      </AnimationContainer>
    </Container>,
    document.body
  );
};

NotificationSlider.propTypes = {
  show: PropTypes.bool,
  duration: PropTypes.number,
  animationDuration: PropTypes.number,
  children: PropTypes.node,
  icon: PropTypes.node,
  heading: PropTypes.string,
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  onClose: PropTypes.func,
  mode: PropTypes.string,
  zIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NotificationSlider.defaultProps = {
  show: false,
  duration: 6000,
  animationDuration: 500,
  children: null,
  icon: null,
  heading: null,
  text: null,
  onClose: () => {},
  mode: 'mobile',
  zIndex: 9,
};

export default NotificationSlider;
